export type AltUpdateDecision = {
  shouldUpdate: boolean;
  newAlt: string;
};

// ============================================================================
// Detection Patterns
// ============================================================================

const SCREENSHOT_PATTERNS: readonly RegExp[] = Object.freeze([
  /screenshot/i,
  /\bScreenshot[_-]?\d{4}[-_]\d{2}[-_]\d{2}/i,
  /screen[\s_-]?shot/i,
  /capture[\s_-]?d'écran/i,
]);

const MOBILE_PHOTO_PATTERNS: readonly RegExp[] = Object.freeze([
  /\bIMG[_-]?\d{4,}\b/i,
  /\bDSC[_-]?\d{4,}\b/i,
  /\bPXL[_-]?\d{8,}\b/i,
  /\bMVIMG[_-]?\d{8,}\b/i,
  /\bVID[_-]?\d{4,}\b/i,
  /\bPSX[_-]?\d{8,}\b/i,
  /\bDCIM\b/i,
  /\bCamera\b/i,
]);

const SOCIAL_MEDIA_PATTERNS: readonly RegExp[] = Object.freeze([
  /whatsapp/i,
  /\bWA\d{4,}\b/i,
  /\bIMG-\d{8}-WA\d{4,}\b/i,
  /facebook/i,
  /\bFB[_-]?IMG/i,
  /instagram/i,
  /snapchat/i,
  /twitter/i,
  /tiktok/i,
  /telegram/i,
  /messenger/i,
]);

// URL pattern (http, https, ftp, file paths)
const URL_PATTERN = /^(https?:\/\/|ftp:\/\/|www\.|[a-z]:[\\/ ])/i;

// Hash-like pattern (32+ hex characters, common in MD5, SHA hashes, temp files)
const HASH_PATTERN = /^[a-f0-9]{32,}$/i;

// UUID pattern (standard UUID format)
const UUID_PATTERN =
  /^[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{12}$/i;

// Only numbers pattern
const ONLY_NUMBERS_PATTERN = /^[\d\s_-]+$/;

// Only special characters and numbers (no letters)
const NO_LETTERS_PATTERN = /^[^a-zA-Z]+$/;

// ============================================================================
// Pattern Detection Functions
// ============================================================================

function matchesScreenshot(input: string): boolean {
  return SCREENSHOT_PATTERNS.some((pattern) => pattern.test(input));
}

function matchesMobilePhoto(input: string): boolean {
  return MOBILE_PHOTO_PATTERNS.some((pattern) => pattern.test(input));
}

function matchesSocialMedia(input: string): boolean {
  return SOCIAL_MEDIA_PATTERNS.some((pattern) => pattern.test(input));
}

function matchesURL(input: string): boolean {
  return URL_PATTERN.test(input);
}

function matchesHash(input: string): boolean {
  const cleaned = input.replace(/\s+/g, "");
  return HASH_PATTERN.test(cleaned);
}

function matchesUUID(input: string): boolean {
  const cleaned = input.replace(/\s+/g, "");
  return UUID_PATTERN.test(cleaned);
}

function isOnlyNumbers(input: string): boolean {
  return ONLY_NUMBERS_PATTERN.test(input);
}

function isOnlyNumbersAndSpecialChars(input: string): boolean {
  return NO_LETTERS_PATTERN.test(input) && input.length > 0;
}

/**
 * Checks if the alt text is auto-generated or low quality.
 * Returns true if the input matches any of these patterns:
 * - Screenshots
 * - Mobile camera photos (IMG_1234, DSC_5678, etc.)
 * - Social media saved images (WhatsApp, Facebook, Instagram, etc.)
 * - URLs (http://, https://, ftp://, www., file paths like C:\)
 * - Hash strings (32+ hex characters like MD5, SHA hashes)
 * - UUID format
 * - Only numbers (with optional spaces/separators)
 * - Only numbers and special characters (no letters)
 */
export function isLowQualityAlt(input: string | null | undefined): boolean {
  if (!input) return true;

  const value = input.trim();
  if (!value) return true;

  if (matchesScreenshot(value)) return true;
  if (matchesMobilePhoto(value)) return true;
  if (matchesSocialMedia(value)) return true;
  if (matchesURL(value)) return true;
  if (matchesHash(value)) return true;
  if (matchesUUID(value)) return true;
  if (isOnlyNumbers(value)) return true;
  if (isOnlyNumbersAndSpecialChars(value)) return true;

  return false;
}

export function shouldReplaceAlt(
  currentAlt: string | null | undefined
): boolean {
  if (isLowQualityAlt(currentAlt)) return true;
  return false;
}

function normalizeText(input: string | undefined | null): string {
  if (!input) return "";
  return input.toLowerCase().trim().replace(/\s+/g, " ");
}

function deduplicateParts(parts: string[]): string[] {
  const result: string[] = [];
  const seen: string[] = [];

  for (const part of parts) {
    const normalized = normalizeText(part);
    if (!normalized) continue;

    const isDuplicate = seen.some(
      (existing) =>
        existing.includes(normalized) || normalized.includes(existing)
    );

    if (!isDuplicate) {
      result.push(part);
      seen.push(normalized);
    }
  }

  return result;
}

export function buildCandidateAlt(name?: string, nameChinese?: string): string {
  const parts = [name?.trim(), nameChinese?.trim()].filter(
    (part): part is string => !!part
  );
  const deduplicated = deduplicateParts(parts);
  return deduplicated.join(" ").trim();
}

/**
 * Decides whether to update the alt text based on quality checks.
 *
 * Returns `shouldUpdate: true` when:
 * 1. A candidate alt can be built from `name` and/or `nameChinese`
 * Low quality patterns include:
 * - Screenshots
 * - Mobile camera photos (IMG_1234, DSC_5678, etc.)
 * - Social media saved images (WhatsApp, Facebook, Instagram, etc.)
 * - URLs (http://, https://, ftp://, www., file paths)
 * - Hash strings (32+ hex characters like MD5, SHA hashes)
 * - UUID format
 * - Only numbers (with optional spaces/separators)
 * - Only numbers and special characters (no letters)
 */
export function decideAltUpdate(
  currentAlt: string | undefined | null,
  name?: string | null,
  nameChinese?: string | null
): AltUpdateDecision {
  const currentValue = (currentAlt ?? "").trim();
  const candidateAlt = buildCandidateAlt(
    name ?? undefined,
    nameChinese ?? undefined
  );

  if (!candidateAlt) {
    return { shouldUpdate: false, newAlt: currentValue };
  }

  if (shouldReplaceAlt(currentValue)) {
    return { shouldUpdate: true, newAlt: candidateAlt };
  }

  return { shouldUpdate: false, newAlt: currentValue };
}

export default decideAltUpdate;
